import React from "react";
import ACCENT_SECTION_STYLE from "./accentSectionStyle";

const HowItWorksSection = () => (
  <section
    className="faqs"
    id="how-it-works"
    aria-labelledby="how-it-works-title"
    style={ACCENT_SECTION_STYLE}
  >
    <div className="container">
      <div className="section-head">
        <span className="section-num">02</span>
        <h2 className="section-title" id="how-it-works-title">
          How it <em>actually works</em>.
        </h2>
      </div>

      <div className="faq-list">
        <details className="faq-item">
          <summary>
            How is 42courses different from other online learning platforms?
          </summary>
          <div className="faq-answer">
            <p>Three meaningful differences.</p>
            <p>
              <strong>One:</strong> every course is personalised by AI to your
              role, location and goals, while keeping all facts and video
              content factually correct. <strong>Two:</strong> your written work
              is marked by a real human, not an algorithm, usually within 45
              minutes. <strong>Three:</strong> lessons are designed using
              behavioural science and gamification to be enjoyable.
            </p>
            <p>
              Which is why our completion rate is around 80% versus an industry
              average of about 10%.
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>How is each course structured?</summary>
          <div className="faq-answer">
            <p>
              We've thrown the traditional textbook out the window. Each course
              is split into <strong>chapters</strong> (key topics you'll need to
              master) and <strong>lessons</strong> (bite-sized 15 to 20-minute
              chunks within each chapter).
            </p>
            <p>
              Every lesson blends a visual element (a video or image to explain
              the subject), written text using storytelling principles, and
              bonus links if you want to go deeper. At the end of each lesson,
              there's a question to test your understanding. Some are
              auto-marked instantly; others get marked by a human within roughly
              45 minutes for proper feedback.
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>How does the AI personalisation work?</summary>
          <div className="faq-answer">
            <p>
              When you start a course, you tell us your role, your location, and
              what you're hoping to get out of it. Our AI then quietly{" "}
              <strong>rewrites the entire lesson around you</strong>, while
              keeping all the underlying data, frameworks and hard facts intact.
            </p>
            <p>
              That means the examples are tailored to your industry, location,
              role and goals, so a marketer in Singapore and a founder in
              Toronto get the same course, but it feels like it was written for
              them.
            </p>
            <p>
              It also means each lesson ends with{" "}
              <strong>two, three or four practical ways</strong> you can apply
              what you've just learned to your own work, your own context, and
              your own goals. So you don't just learn the idea, you leave with a
              clear next move.{" "}
              <strong>
                We were the first elearning platform to build this.
              </strong>
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>Do humans really mark my work?</summary>
          <div className="faq-answer">
            <p>
              Yes. Real people read every assignment you submit and write back
              with personal feedback, usually within <strong>45 minutes</strong>
              . We use AI to support our markers (catching typos, surfacing
              trends), but the actual feedback you read is written by a human.
            </p>
            <p>
              We're less interested in whether you're "strictly right or wrong"
              and more interested in your thinking, your ideas, and your effort.
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>
            How does gamification work? (XP, lives, badges, leaderboards)
          </summary>
          <div className="faq-answer">
            <p>
              We use lessons from behavioural science, advertising, and gaming
              to nudge you toward finishing what you start. Three things make
              this work:
            </p>
            <p>
              <strong>XP (points):</strong> the better your answers, and the
              more other learners like them, the more points you earn. We
              display these on global, country, and per-course leaderboards.
              Companies on team plans get private team leaderboards too.{" "}
              <a
                href="https://42courses-course-material.s3.us-west-1.amazonaws.com/FAQs+Page/42courses+XP.pdf"
                target="_blank"
                rel="noopener"
              >
                See exactly how points are allocated &rarr;
              </a>
            </p>
            <p>
              <strong>Lives:</strong> you start each course with 6 lives. Use
              them to retake a lesson question you got wrong. Once you run out,
              you can keep going, but you can no longer retake questions.
            </p>
            <p>
              <strong>Badges:</strong> awarded for milestones (passing courses,
              getting many lessons right in a row). The rarest is the 42
              Ultimate Answer Badge, given for genuinely brilliant submissions.
            </p>
            <p>
              The point of all this is that learners are roughly{" "}
              <strong>10x more likely to finish</strong> a course on 42courses
              than on a typical platform.
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>
            Can I really learn from people like Rory Sutherland and Dan Ariely?
          </summary>
          <div className="faq-answer">
            <p>
              Yes, directly. Each instructor records their own lessons and works
              with our team to develop the course content.{" "}
              <strong>
                You're not getting a course producer reading from a script
              </strong>
              , and you're not getting a recycled YouTube talk. You're getting
              the actual practitioners explaining how they actually think, in
              lessons designed around how people actually learn.
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>
            Will the courses still be relevant in a few years given how fast AI
            changes?
          </summary>
          <div className="faq-answer">
            <p>
              Yes, that is exactly the point. We focus on the{" "}
              <strong>core, transferable skills</strong>: storytelling,
              behavioural science, persuasion, strategy, creativity, leadership,
              asking better questions. The tools change every Tuesday. The
              thinking does not.
            </p>
          </div>
        </details>

        <details className="faq-item">
          <summary>What if I get stuck on a lesson?</summary>
          <div className="faq-answer">
            <p>
              Ask a question in our Community Hub, where other learners can chip
              in. Or email our team directly at{" "}
              <a href="mailto:groundcontrol@42courses.com">
                groundcontrol@42courses.com
              </a>
              . We reply within 24 hours, often sooner.
            </p>
          </div>
        </details>
      </div>
    </div>
  </section>
);

export default HowItWorksSection;
